import React, { useState } from 'react';
import { X, Save, Sparkles, Loader2 } from 'lucide-react';
import { authAxios, apiErrorMessage } from '@/lib/api';
import { MultiImageUploader } from './ImageUploaders';
import ProductSpecs from './product/ProductSpecs';
import ProductVideos from './product/ProductVideos';
import BrochureUpload from './product/BrochureUpload';

const LABEL_CLS = 'text-[10px] uppercase tracking-wider text-earth/50 font-sans font-semibold';
const INPUT_CLS = 'w-full border border-earth/15 rounded-md px-3 py-2.5 text-sm font-sans text-earth-dark focus:ring-2 focus:ring-saffron/30 focus:border-saffron focus:outline-none bg-cream/30';

const initialForm = (product) => ({
  name: product?.name || '',
  tagline: product?.tagline || '',
  description: product?.description || '',
  image: product?.image || '',
  photos: product?.photos || [],
  videos: product?.videos || [],
  brochure_url: product?.brochure_url || '',
  color: product?.color || '#D4870A',
  category: product?.category || 'powder',
  specs: product?.specs || {},
});

function TextField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className={`${LABEL_CLS} mb-1 block`}>{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} className={INPUT_CLS} data-testid={testId} />
    </div>
  );
}

function DescriptionField({ form, onGenerated, onChange }) {
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await authAxios.post('/api/ai/generate-description', {
        product_name: form.name,
        specs: form.specs,
        existing_description: form.description,
      });
      onGenerated(data);
    } catch (e) {
      alert('AI generation failed: ' + apiErrorMessage(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className={LABEL_CLS}>Description</label>
        <button onClick={generate} disabled={generating || !form.name} className="flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-sans font-semibold text-[10px] px-2.5 py-1.5 rounded-md hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-40 shadow-sm" data-testid="ai-description-btn">
          {generating ? <><Loader2 size={10} className="animate-spin" /> Generating...</> : <><Sparkles size={10} /> AI Generate</>}
        </button>
      </div>
      <textarea value={form.description} onChange={(e) => onChange(e.target.value)} rows={3} className={`${INPUT_CLS} resize-none`} data-testid="editor-description" />
    </div>
  );
}

export default function ProductEditor({ product, onSave, onCancel }) {
  const [form, setForm] = useState(() => initialForm(product));
  const [saving, setSaving] = useState(false);
  const setField = (key) => (value) => setForm(f => ({ ...f, [key]: value }));
  const updatePhotos = (updater) => setForm(f => {
    const photos = updater(f.photos);
    return { ...f, photos, image: photos[0] || '' };
  });
  const updateVideos = (updater) => setForm(f => ({ ...f, videos: updater(f.videos) }));

  const applyAi = (data) => setForm(f => ({ ...f, description: data.description || f.description, tagline: data.tagline || f.tagline }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ ...form, image: form.image || form.photos[0] || '' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-saffron-100/50 shadow-card-lift p-6 space-y-5" data-testid="product-editor">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-earth-dark">{product ? 'Edit Product' : 'New Product'}</h3>
        <button onClick={onCancel} className="text-earth/40 hover:text-earth transition-colors" data-testid="editor-cancel"><X size={18} /></button>
      </div>

      <MultiImageUploader photos={form.photos} onChange={updatePhotos} label="Product Photos (Drag & Drop)" />

      <div className="grid grid-cols-2 gap-4">
        <TextField label="Product Name" value={form.name} onChange={setField('name')} testId="editor-name" />
        <TextField label="Tagline" value={form.tagline} onChange={setField('tagline')} testId="editor-tagline" />
      </div>

      <DescriptionField form={form} onGenerated={applyAi} onChange={setField('description')} />

      <div className="grid grid-cols-2 gap-4">
        <TextField label="Category" value={form.category} onChange={setField('category')} testId="editor-category" />
        <div>
          <label className={`${LABEL_CLS} mb-1 block`}>Color</label>
          <div className="flex gap-2 items-center">
            <input type="color" value={form.color} onChange={(e) => setField('color')(e.target.value)} className="w-10 h-10 rounded border-0 cursor-pointer" data-testid="editor-color-picker" />
            <input value={form.color} onChange={(e) => setField('color')(e.target.value)} className={`${INPUT_CLS} flex-1`} data-testid="editor-color" />
          </div>
        </div>
      </div>

      <ProductSpecs specs={form.specs} onChange={setField('specs')} />
      <ProductVideos videos={form.videos} onChange={updateVideos} />
      <BrochureUpload url={form.brochure_url} onChange={setField('brochure_url')} />

      <button onClick={handleSave} disabled={saving} className="w-full bg-saffron text-white font-sans font-semibold text-sm py-3 rounded-md hover:bg-saffron-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="editor-save">
        <Save size={14} /> {saving ? 'Saving...' : 'Save Product'}
      </button>
    </div>
  );
}
